import AnalyticsService from 'Faster/AnalyticsService';
import FasterConfiguration from 'Faster/FasterConfiguration';
import FasterDevice from './FasterDevice';
import FasterSession from './FasterSession';
import FasterEvent from './FasterEvent';
import ServerDate from './model/ServerDate';
import OpenEventBuilder from './event/OpenEventBuilder';
import FasterGeo from './FasterGeo';

const _isNotBrowser = typeof window === 'undefined' || typeof document === 'undefined';

export default class FasterAnalytics {
  private static presetConfiguration?: FasterConfiguration = null;
  private static instance?: FasterAnalytics = null;
  public readonly device: FasterDevice;
  public readonly session: FasterSession;
  public readonly event: FasterEvent;
  public readonly geo: FasterGeo;
  private analyticsService: AnalyticsService;

  private constructor(analyticsService: AnalyticsService) {
    this.device = new FasterDevice(analyticsService.deviceService);
    this.session = new FasterSession(analyticsService.sessionService);
    this.event = new FasterEvent(analyticsService.eventService);
    this.geo = new FasterGeo(analyticsService.geoService);
    this.analyticsService = analyticsService;
  }

  public static start(trackInstall = true): FasterAnalytics {
    return FasterAnalytics.init(trackInstall);
  }

  public static startEmbedded(externalDeviceId: string, externalSessionId: string): FasterAnalytics {
    return FasterAnalytics.init(false, externalDeviceId, externalSessionId);
  }

  private static init(trackInstall = true, externalDeviceId?: string, externalSessionId?: string): FasterAnalytics {
    try {
      if (_isNotBrowser) {
        throw Error('Faster only initializes in browser environments');
      }

      if (FasterAnalytics.presetConfiguration === null) {
        throw Error('Faster must be first configured before start up');
      }

      if (FasterAnalytics.instance === null) {
        const analyticsService = new AnalyticsService(
          FasterAnalytics.presetConfiguration,
          externalDeviceId,
          externalSessionId
        );

        ServerDate.getInstance(analyticsService.serverDateService).sync();

        analyticsService.init(trackInstall);

        FasterAnalytics.instance = new FasterAnalytics(analyticsService);

        if (!externalDeviceId || !externalSessionId) {
          try {
            this.sendOpenEvent(analyticsService);
          } catch {
            throw Error('Failed to send open event on init of Faster Analytics SDK');
          }
        }
      }
      return FasterAnalytics.instance;
    } catch (err) {
      throw Error(`Failed to initialize Faster Analytics SDK: ${err.message}`);
    }
  }

  public static configure(configuration: FasterConfiguration): typeof FasterAnalytics {
    this.presetConfiguration = configuration;

    return this;
  }

  private static async sendOpenEvent(analyticsService: AnalyticsService): Promise<void> {
    const openEvent = new OpenEventBuilder(false).build();
    analyticsService.eventService.sendEvent(openEvent);
  }

  public static clear() {
    FasterAnalytics.instance = null;
    FasterAnalytics.presetConfiguration = null;
  }
}
